// Enums for type-safe string constants across the Doctor app

/**
 * Gender options for patients
 */
export enum Gender {
  MALE = 'Male',
  FEMALE = 'Female',
  OTHER = 'Other'
}

/**
 * Gender values from backend
 */
export enum ApiGender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  OTHER = 'OTHER'
}

/**
 * Blood type options
 */
export enum BloodType {
  A_POSITIVE = 'A+',
  A_NEGATIVE = 'A-',
  B_POSITIVE = 'B+',
  B_NEGATIVE = 'B-',
  AB_POSITIVE = 'AB+',
  AB_NEGATIVE = 'AB-',
  O_POSITIVE = 'O+',
  O_NEGATIVE = 'O-'
}

/**
 * Blood type values from backend
 */
export enum ApiBloodType {
  A_POSITIVE = 'A_POSITIVE',
  A_NEGATIVE = 'A_NEGATIVE',
  B_POSITIVE = 'B_POSITIVE',
  B_NEGATIVE = 'B_NEGATIVE',
  AB_POSITIVE = 'AB_POSITIVE',
  AB_NEGATIVE = 'AB_NEGATIVE',
  O_POSITIVE = 'O_POSITIVE',
  O_NEGATIVE = 'O_NEGATIVE'
}

/**
 * Appointment status
 */
export enum AppointmentStatus {
  SCHEDULED = 'Scheduled',
  PENDING = 'Pending',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
  NO_SHOW = 'No-Show'
}

/**
 * Appointment status values from backend
 */
export enum ApiAppointmentStatus {
  SCHEDULED = 'SCHEDULED',
  CONFIRMED = 'CONFIRMED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  NO_SHOW = 'NO_SHOW'
}


/**
 * Authentication status
 */
export enum AuthStatus {
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
  LOADING = 'loading'
}

/**
 * Form field types for validation
 */
export enum FormFieldType {
  EMAIL = 'email',
  PASSWORD = 'password',
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  SELECT = 'select',
  TEXTAREA = 'textarea'
}

/**
 * Appointment details tab options
 */
export enum AppointmentTab {
  OVERVIEW = 'overview',
  MEDICAL_HISTORY = 'medical_history',
  CHAT = 'chat',
  PATIENT_UPLOADS = 'patient_uploads'
}

/**
 * Appointment filter tabs
 */
export enum AppointmentFilterTab {
  ALL = 'all',
  TODAY = 'today',
  UPCOMING = 'upcoming',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

/**
 * Appointment sort types
 */
export enum AppointmentSortType {
  TIME = 'time',
  PATIENT_NAME = 'patientName',
  STATUS = 'status',
  TYPE = 'type'
}


/**
 * Main application tab types
 */
export enum TabType {
  DASHBOARD = 'dashboard',
  PATIENTS = 'patients',
  APPOINTMENTS = 'appointments',
  INVOICES = 'invoices',
  AI_TRIAGE = 'ai-triage',
  CHAT = 'chat',
  MY_REVIEWS = 'my-reviews',
  NOTIFICATIONS = 'notifications',
  SETTINGS = 'settings'
}

/**
 * Patient detail page tabs
 */
export enum PatientTab {
  OVERVIEW = 'overview',
  MEDICAL_HISTORY = 'medical-history',
  CHANGE_REQUESTS = 'change-requests'
}

/**
 * Settings page tabs
 */
export enum SettingsTab {
  PROFILE = 'profile',
  ACCOUNT = 'account',
  PREFERENCES = 'preferences'
}

/**
 * Consultation tabs
 */
export enum ConsultationTab {
  MEDICAL_HISTORY = 'medical-history',
  SYMPTOMS = 'symptoms',
  TREATMENT = 'treatment',
  FOLLOWUP = 'followup'
}

/**
 * Theme modes
 */
export enum ThemeMode {
  LIGHT = 'light',
  DARK = 'dark',
  AUTO = 'auto'
}

/**
 * Auth page view states
 */
export enum AuthView {
  SIGN_IN = 'sign_in',
  REGISTER = 'register',
  COMPLETE_PROFILE = 'complete_profile',
}

/**
 * Public (unauthenticated) routes for the doctor app
 *
 * NOT_FOUND is a sentinel value used by the router to render the 404 page
 * for any path that doesn't match a known route (audit B503). It's
 * intentionally not a real path — visiting `/not-found` directly is treated
 * the same as any other unknown URL.
 */
export enum PublicRoute {
  LANDING = '/',
  LOGIN = '/login',
  SIGNUP = '/signup',
  DOCTOR_SIGNUP = '/doctor/signup',
  PATIENT_SIGNUP = '/patient/signup',
  NOT_FOUND = '__not_found__',
}

/**
 * Anchor IDs for the landing page sections (used for in-page nav)
 */
export enum LandingSection {
  HERO = 'hero',
  FEATURES = 'features',
  HOW_IT_WORKS = 'how-it-works',
  FOR_DOCTORS = 'for-doctors',
  TECH = 'tech',
  CONTACT = 'contact',
}

/**
 * Invoice/Payment status for display
 */
export enum InvoiceStatus {
  PENDING = 'Pending',
  PAID = 'Paid',
  FAILED = 'Failed',
  REFUNDED = 'Refunded',
  GENERATED = 'Generated',
}

/**
 * Payment status values from backend API
 */
export enum ApiPaymentStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
}

/**
 * Helper functions to get enum values as arrays
 */
export const EnumHelpers = {
  getGenderValues: (): string[] => Object.values(Gender),
  getBloodTypeValues: (): string[] => Object.values(BloodType),
  getAppointmentStatusValues: (): string[] => Object.values(AppointmentStatus),
};

/**
 * Where the OCR review modal was opened from — drives behavior on apply
 * (Patient Details merges directly; Consultation pre-fills the active form).
 */
export enum OcrSource {
  PATIENT_DETAILS = 'patient_details',
  CONSULTATION = 'consultation',
}

/**
 * Editable sections inside the OCR review modal.
 */
export enum OcrFieldKind {
  VITALS = 'vitals',
  LAB_VALUES = 'lab_values',
  DIAGNOSES = 'diagnoses',
  MEDICATIONS = 'medications',
  ALLERGIES = 'allergies',
  CONDITIONS = 'conditions',
}

/**
 * Lifecycle states of the OCR upload + review flow.
 */
export enum OcrUploadStatus {
  IDLE = 'idle',
  UPLOADING = 'uploading',
  EXTRACTING = 'extracting',
  REVIEWING = 'reviewing',
  APPLYING = 'applying',
  SUCCESS = 'success',
  ERROR = 'error',
}


export enum FileUploadCategory {
  PROFILE_PICTURE = 'PROFILE_PICTURE',
  TEST_REPORT = 'TEST_REPORT',
  PRESCRIPTION_EXTERNAL = 'PRESCRIPTION_EXTERNAL',
  INSURANCE_DOCUMENT = 'INSURANCE_DOCUMENT',
  ID_PROOF = 'ID_PROOF',
  CHAT_ATTACHMENT = 'CHAT_ATTACHMENT',
  OTHER = 'OTHER',
}

export enum FileUploadLinkKind {
  USER_PROFILE = 'USER_PROFILE',
  PATIENT_RECORD = 'PATIENT_RECORD',
  APPOINTMENT = 'APPOINTMENT',
  CHAT_MESSAGE = 'CHAT_MESSAGE',
  NONE = 'NONE',
}

export enum NotificationType {
  APPOINTMENT_BOOKED = 'APPOINTMENT_BOOKED',
  APPOINTMENT_CANCELLED = 'APPOINTMENT_CANCELLED',
  APPOINTMENT_RESCHEDULED = 'APPOINTMENT_RESCHEDULED',
  APPOINTMENT_STATUS_CHANGED = 'APPOINTMENT_STATUS_CHANGED',
  APPOINTMENT_REMINDER = 'APPOINTMENT_REMINDER',
  PRESCRIPTION_ISSUED = 'PRESCRIPTION_ISSUED',
  CHAT_MESSAGE_RECEIVED = 'CHAT_MESSAGE_RECEIVED',
  CHANGE_REQUEST_CREATED = 'CHANGE_REQUEST_CREATED',
  CHANGE_REQUEST_APPROVED = 'CHANGE_REQUEST_APPROVED',
  CHANGE_REQUEST_REJECTED = 'CHANGE_REQUEST_REJECTED',
  REVIEW_RECEIVED = 'REVIEW_RECEIVED',
  PROFILE_UPDATED = 'PROFILE_UPDATED',
  AI_DRAFT_SUBMITTED = 'AI_DRAFT_SUBMITTED',
  AI_DRAFT_APPROVED = 'AI_DRAFT_APPROVED',
  AI_DRAFT_REJECTED = 'AI_DRAFT_REJECTED',
  AI_DRAFT_NEEDS_MORE_INFO = 'AI_DRAFT_NEEDS_MORE_INFO',
}

export enum NotificationChannel {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  IN_APP = 'IN_APP',
}

export enum ChangeRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  WITHDRAWN = 'WITHDRAWN',
}

export enum ChangeRequestField {
  ALLERGIES = 'ALLERGIES',
  CURRENT_MEDICATIONS = 'CURRENT_MEDICATIONS',
  CONDITIONS = 'CONDITIONS',
  SURGERIES = 'SURGERIES',
  FAMILY_HISTORY = 'FAMILY_HISTORY',
  LIFESTYLE = 'LIFESTYLE',
  BLOOD_TYPE = 'BLOOD_TYPE',
  EMERGENCY_CONTACT = 'EMERGENCY_CONTACT',
}

export enum ChatMessageContentType {
  TEXT = 'TEXT',
  FILE = 'FILE',
  SYSTEM = 'SYSTEM',
  SYSTEM_AI_DRAFT = 'SYSTEM_AI_DRAFT',
  SYSTEM_AI_STATUS = 'SYSTEM_AI_STATUS',
  SYSTEM_DIAGNOSIS = 'SYSTEM_DIAGNOSIS',
  SYSTEM_PRESCRIPTION = 'SYSTEM_PRESCRIPTION',
  SYSTEM_DOCTOR_NOTE = 'SYSTEM_DOCTOR_NOTE',
}

export enum AiConsultationStatus {
  DRAFT_IN_PROGRESS = 'DRAFT_IN_PROGRESS',
  AWAITING_REVIEW = 'AWAITING_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  NEEDS_MORE_INFO = 'NEEDS_MORE_INFO',
  CANCELLED = 'CANCELLED',
}

export enum AiConsultationUrgency {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  EMERGENCY = 'EMERGENCY',
}

export enum ChatThreadKind {
  AI_TRIAGE = 'AI_TRIAGE',
  CONSULTATION = 'CONSULTATION',
}
